import React from 'react';
import {
  InformationCircleIcon,
  TicketIcon,
} from '@heroicons/react/24/outline';
import {
  hasTaxFeesBreakdown as hasTaxFeesBreakdownFor,
  taxAndFeesTotal,
  extraFeeRows,
} from '../../../../utils/orderTotals';

const cn = (...c) => c.filter(Boolean).join(' ');

const usd = (n) =>
  Number(n || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });

const Row = ({ label, value, onLabelClick, withIcon, accent, valueClass }) => (
  <div className="flex items-center justify-between gap-3 px-5 py-2.5">
    <div
      className={cn(
        'flex items-center gap-1 text-sm',
        onLabelClick &&
          'cursor-pointer hover:text-blue-600 dark:hover:text-sky-400',
        accent
          ? 'font-bold text-slate-900 dark:text-slate-100'
          : 'font-medium text-slate-500 dark:text-slate-400',
      )}
      onClick={onLabelClick}
    >
      {label}
      {withIcon && <InformationCircleIcon className="h-4 w-4" />}
    </div>
    <div
      className={cn(
        'tabular-nums text-sm',
        valueClass ||
          (accent
            ? 'font-bold text-slate-900 dark:text-slate-100'
            : 'font-semibold text-slate-700 dark:text-slate-200'),
      )}
    >
      {value}
    </div>
  </div>
);

const TotalsSummary = ({ orderDetail, onClickTaxFees }) => {
  const hasTaxFeesBreakdown = hasTaxFeesBreakdownFor(orderDetail);
  const taxAndFees = taxAndFeesTotal(orderDetail);

  const finalTotal = orderDetail.refund
    ? orderDetail.total - orderDetail.refund
    : orderDetail.total;

  return (
    <section
      className="reveal flex flex-col gap-4 lg:flex-row lg:items-start"
      aria-label="Order totals"
    >
      {orderDetail.note ? (
        <div className="flex-1 rounded-md border border-dashed border-slate-300 bg-white p-5 dark:border-slate-600 dark:bg-slate-800">
          <h3 className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
            Order Note
          </h3>
          <p className="mt-2 text-sm italic text-slate-700 dark:text-slate-300">
            {orderDetail.note}
          </p>
        </div>
      ) : (
        <div className="hidden flex-1 lg:block" aria-hidden="true" />
      )}

      <div className="w-full overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800 lg:w-96 lg:max-w-sm lg:flex-shrink-0">
        <div className="divide-y divide-slate-100 dark:divide-slate-700/60">
          <Row label="Subtotal" value={usd(orderDetail.subtotal)} />
          <Row
            label={hasTaxFeesBreakdown ? 'Sales Tax & Fees' : 'Sales Tax'}
            value={usd(taxAndFees)}
            withIcon={hasTaxFeesBreakdown}
            onLabelClick={hasTaxFeesBreakdown ? onClickTaxFees : null}
          />
          {extraFeeRows(orderDetail).map((fee, i) => (
            <Row key={i} label={fee.name} value={usd(fee.price)} />
          ))}
          {orderDetail.tip !== 0 && (
            <Row label="Tip" value={usd(orderDetail.tip)} />
          )}
          {orderDetail.discount !== 0 && (
            <Row
              label={
                <span className="flex items-center gap-1.5">
                  <TicketIcon className="h-4 w-4 text-emerald-500" />
                  <span>Discount</span>
                  {orderDetail.discountcode && (
                    <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 font-mono text-[0.65rem] font-semibold uppercase tracking-wider text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-900/20 dark:text-emerald-300">
                      {orderDetail.discountcode}
                    </span>
                  )}
                </span>
              }
              value={`− ${usd(orderDetail.discount)}`}
              valueClass="font-semibold tabular-nums text-emerald-600 dark:text-emerald-400 text-sm"
            />
          )}
          {orderDetail.refund !== 0 && (
            <Row label="Refund" value={`− ${usd(orderDetail.refund)}`} />
          )}
          <Row label="Total" value={usd(finalTotal)} accent />
        </div>
      </div>
    </section>
  );
};

export default TotalsSummary;
